import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Button,
  TextField,
  Typography,
  IconButton,
  Box,
  Alert,
} from '@mui/material';
import AddPhotoAlternateOutlined from '@mui/icons-material/AddPhotoAlternateOutlined';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import { buildApiUrl } from '../config';
import { AppDialog } from './dialog';

const MAX_IMAGE_FILES = 5;
const MAX_BYTES_PER_FILE = 4 * 1024 * 1024;
const MAX_TOTAL_BYTES = 15 * 1024 * 1024;

type AttachmentItem = { id: string; file: File; previewUrl: string };

export interface SupportRequestDialogProps {
  open: boolean;
  onClose: () => void;
  tenantId?: string | null;
  onSuccess?: () => void;
}

function isImageFile(f: File): boolean {
  return f.type.startsWith('image/');
}

/**
 * Help & support: subject + message + optional image attachments → API (saved + emailed to team).
 */
export default function SupportRequestDialog({ open, onClose, tenantId, onSuccess }: SupportRequestDialogProps) {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState<AttachmentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const revokePreviews = useCallback((items: AttachmentItem[]) => {
    items.forEach((a) => {
      try {
        URL.revokeObjectURL(a.previewUrl);
      } catch {
        /* ignore */
      }
    });
  }, []);

  useEffect(() => {
    if (!open) {
      setTitle('');
      setMessage('');
      setError(null);
      setLoading(false);
      setAttachments((prev) => {
        revokePreviews(prev);
        return [];
      });
    }
  }, [open, revokePreviews]);

  const canSend = title.trim().length > 0 && message.trim().length > 0 && !loading;

  const addFiles = (fileList: FileList | null) => {
    if (!fileList?.length) return;
    setError(null);
    let validationError: string | null = null;
    setAttachments((prev) => {
      const next = [...prev];
      let total = prev.reduce((n, a) => n + a.file.size, 0);
      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];
        if (!isImageFile(file)) {
          validationError = `"${file.name}" is not an image. Only image files are allowed.`;
          continue;
        }
        if (file.size > MAX_BYTES_PER_FILE) {
          validationError = `"${file.name}" is too large (max 4 MB per image).`;
          continue;
        }
        if (next.length >= MAX_IMAGE_FILES) {
          validationError = `You can attach at most ${MAX_IMAGE_FILES} images.`;
          break;
        }
        if (total + file.size > MAX_TOTAL_BYTES) {
          validationError = 'Total size of images must be 15 MB or less.';
          break;
        }
        total += file.size;
        next.push({
          id: `${Date.now()}-${i}-${file.name}`,
          file,
          previewUrl: URL.createObjectURL(file),
        });
      }
      return next;
    });
    if (validationError) setError(validationError);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (id: string) => {
    setAttachments((prev) => {
      const item = prev.find((a) => a.id === id);
      if (item) {
        try {
          URL.revokeObjectURL(item.previewUrl);
        } catch {
          /* ignore */
        }
      }
      return prev.filter((a) => a.id !== id);
    });
  };

  const handleSend = async () => {
    if (!canSend) return;
    const tid = tenantId?.trim();
    if (!tid) {
      setError('Missing tenant. Try signing in again.');
      return;
    }
    const token = localStorage.getItem('klaris_jwt');
    if (!token) {
      setError('Not signed in.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('title', title.trim());
      fd.append('message', message.trim());
      try {
        fd.append('client_path', window.location.pathname + window.location.search);
      } catch {
        /* ignore */
      }
      attachments.forEach(({ file }) => {
        fd.append('files', file, file.name);
      });

      const resp = await fetch(buildApiUrl(`/api/v1/tenants/${encodeURIComponent(tid)}/support/contact`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const detail = data?.detail;
        const msg =
          typeof detail === 'string'
            ? detail
            : Array.isArray(detail)
              ? detail.map((d: { msg?: string }) => d?.msg).filter(Boolean).join('; ')
              : 'Could not send your message. Please try again.';
        throw new Error(msg);
      }
      onSuccess?.();
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="sm"
      title="Help & support"
      busy={loading}
      primaryAction={{
        label: 'Send message',
        onClick: () => void handleSend(),
        disabled: !title.trim() || !message.trim(),
      }}
      secondaryAction={{ label: 'Cancel', color: 'inherit', onClick: onClose }}
    >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.6 }}>
          Describe what you were doing and what went wrong. You can attach screenshots (images only). We read
          every message.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Subject"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
            size="small"
            placeholder="Short summary of the issue"
            disabled={loading}
            autoFocus
          />
          <TextField
            label="Message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            fullWidth
            required
            multiline
            minRows={5}
            maxRows={14}
            disabled={loading}
            placeholder="Steps to reproduce, what you expected, and any error text you saw…"
            sx={{
              '& textarea': { lineHeight: 1.5 },
            }}
          />

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={(e) => addFiles(e.target.files)}
          />
          <Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddPhotoAlternateOutlined />}
              onClick={() => fileInputRef.current?.click()}
              disabled={loading || attachments.length >= MAX_IMAGE_FILES}
              sx={{ textTransform: 'none', mb: attachments.length ? 1.5 : 0 }}
            >
              Attach images ({attachments.length}/{MAX_IMAGE_FILES})
            </Button>
            <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
              PNG, JPEG, GIF, WebP, etc. · up to 4 MB each · 15 MB total
            </Typography>
          </Box>

          {attachments.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {attachments.map((a) => (
                <Box
                  key={a.id}
                  sx={{
                    position: 'relative',
                    width: 88,
                    height: 88,
                    borderRadius: 1,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="img"
                    src={a.previewUrl}
                    alt=""
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                  <IconButton
                    size="small"
                    aria-label="Remove image"
                    onClick={() => removeAttachment(a.id)}
                    disabled={loading}
                    sx={{
                      position: 'absolute',
                      top: 2,
                      right: 2,
                      bgcolor: 'rgba(0,0,0,0.45)',
                      color: '#fff',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
                      p: 0.35,
                    }}
                  >
                    <DeleteOutlineRounded sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </Box>
    </AppDialog>
  );
}
